import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from '@/common/ui/button'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { UseFormReturn } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { BankSelector } from '../BankSelector'
import { RequiredIndicator } from '@/common/ui/form/required-indicator'
import { formSchema, FormValues } from './BankAccountFormSchema'
import React, { useEffect } from 'react'
import { BankAccountType } from '@/schema'

interface Props {
	defaultValues?: Partial<FormValues>
	onSubmit: (values: FormValues, form: UseFormReturn<FormValues>) => void
	mode: 'create' | 'edit'
	serverFieldErrors?: Record<string, string>
}

export default function BankAccountForm({
	defaultValues,
	onSubmit,
	mode,
	serverFieldErrors,
}: Props) {
	const navigate = useNavigate()

	const form = useForm<FormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			accountName: defaultValues?.accountName || '',
			accountNumber: defaultValues?.accountNumber || '',
			bankProvider: defaultValues?.bankProvider || '',
			bankType: defaultValues?.bankType ?? BankAccountType.DEPOSIT,
			isActive: defaultValues?.isActive ?? true,
			validateType: defaultValues?.validateType || [],
			bankApiCredential: defaultValues?.bankApiCredential,
			appCredential: defaultValues?.appCredential,
		},
	})

	useEffect(() => {
		if (serverFieldErrors && Object.keys(serverFieldErrors).length > 0) {
			Object.entries(serverFieldErrors).forEach(([field, message]) => {
				form.setError(field as keyof FormValues, {
					type: 'server',
					message,
				})
			})
		}
	}, [serverFieldErrors, form])

	const validateType = form.watch('validateType')
	const showBankApiFields = validateType.includes('BANK_API')
	const showBankAppFields = validateType.includes('BANK_APP')

	// Add effect to clear credentials when validation type changes
	React.useEffect(() => {
		if (!showBankApiFields) {
			form.setValue('bankApiCredential', undefined)
		}
		if (!showBankAppFields) {
			form.setValue('appCredential', undefined)
		}
	}, [validateType, form])

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()

		const values = form.getValues()

		onSubmit(values, form)
	}

	return (
		<Form {...form}>
			<form onSubmit={handleSubmit} className="space-y-8">
				<div className="max-w-md space-y-8">
					<FormField
						control={form.control}
						name="accountName"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									<RequiredIndicator /> ชื่อบัญชี
								</FormLabel>
								<FormControl>
									<Input placeholder="กรอกชื่อบัญชี" {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="accountNumber"
						render={({ field }) => {
							const selectedBank = form.watch('bankProvider')
							const isTrueMoney = selectedBank === 'TrueMoney'
							const label = isTrueMoney ? 'เบอร์โทรศัพท์' : 'เลขบัญชี'
							const placeholder = isTrueMoney
								? 'กรอกเบอร์โทรศัพท์'
								: 'กรอกเลขบัญชี'

							return (
								<FormItem>
									<FormLabel>
										<RequiredIndicator /> {label}
									</FormLabel>
									<FormControl>
										<Input
											placeholder={placeholder}
											type="number"
											onKeyDown={(e) => {
												if (['e', 'E', '+', '-'].includes(e.key)) {
													e.preventDefault()
												}
											}}
											{...field}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)
						}}
					/>

					<BankSelector control={form.control} label="ธนาคาร" />
					{/* 
					<FormField
						control={form.control}
						name="bankType"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									<RequiredIndicator /> ประเภทบัญชี
								</FormLabel>
								<Select
									onValueChange={field.onChange}
									defaultValue={field.value}
								>
									<FormControl>
										<SelectTrigger>
											<SelectValue placeholder="เลือกประเภทบัญชี" />
										</SelectTrigger>
									</FormControl>
									<SelectContent>
										{BANK_ACCOUNT_TYPES.map((type) => (
											<SelectItem key={type.value} value={type.value}>
												{type.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="validateType"
						render={() => (
							<FormItem>
								<FormLabel>
									<RequiredIndicator /> ประเภทการตรวจสอบ
								</FormLabel>
								<div className="space-y-2">
									{VALIDATION_TYPES.map((type) => (
										<FormField
											key={type.value}
											control={form.control}
											name="validateType"
											render={({ field }) => {
												return (
													<FormItem
														key={type.value}
														className="flex flex-row items-start space-x-3 space-y-0"
													>
														<FormControl>
															<Checkbox
																checked={field.value?.includes(type.value)}
																onCheckedChange={(checked) => {
																	const updatedTypes = checked
																		? [...(field.value || []), type.value]
																		: field.value?.filter(
																				(value) => value !== type.value,
																			) || []
																	field.onChange(updatedTypes)
																}}
															/>
														</FormControl>
														<FormLabel className="font-normal">
															{type.label}
														</FormLabel>
													</FormItem>
												)
											}}
										/>
									))}
								</div>
								<FormMessage />
							</FormItem>
						)}
					/>

					{showBankApiFields && (
						<div className="space-y-4">
							<FormField
								control={form.control}
								name="bankApiCredential.username"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											<RequiredIndicator /> API Username
										</FormLabel>
										<FormControl>
											<Input placeholder="กรอก username" {...field} />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="bankApiCredential.password"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											<RequiredIndicator /> API Password
										</FormLabel>
										<FormControl>
											<Input
												type="password"
												placeholder="กรอก password"
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					)}

					{showBankAppFields && (
						<div className="space-y-4">
							<FormField
								control={form.control}
								name="appCredential.deviceId"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Device ID</FormLabel>
										<FormControl>
											<Input placeholder="กรอก Device ID" {...field} />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="appCredential.pin"
								render={({ field }) => (
									<FormItem>
										<FormLabel>PIN</FormLabel>
										<FormControl>
											<Input
												type="password"
												placeholder="กรอก PIN"
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					)} */}

					<div className="flex gap-4">
						<Button type="submit" variant="default">
							{mode === 'create' ? 'สร้างบัญชีธนาคาร' : 'แก้ไขบัญชีธนาคาร'}
						</Button>
						<Button
							type="button"
							variant="outline"
							onClick={() => navigate(-1)}
						>
							ยกเลิก
						</Button>
					</div>
				</div>
			</form>
		</Form>
	)
}
